/* eslint-disable no-param-reassign */
/**
 * Outreach.slice.js
 *
 * Manages state for the Outreach module (Influencers, Message Center, Documents, Deliverables).
 * API thunks only — data helpers live in OutreachUtils.js.
 */
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import arClient from '../../../util/api_client';
import { OK, LEGACY_OK, isApiSuccess } from '../../../constants/platformStatusCodes';
import {
  CUSTOM_FETCH_REVISIONS,
  CUSTOM_FETCH_DOCUMENTS,
  CUSTOM_GET_CONVERSATION,
  CUSTOM_ADD_DRAFT,
  CUSTOM_ADD_DRAFT_COMMENT,
  CUSTOM_DELETE_DRAFT,
  CUSTOM_ADD_MESSAGE,
} from '../../../constants/apiUrls';
import stripFroalaAttribution from '../../../util/stripFroalaAttribution';
import { DEFAULT_SENDER_INFO } from './GroupOutreach/constants';
import { OUTREACH_DEFAULT_EMAIL } from './constants';
import {
  emptyMessageCenterOutreachData,
  normalizeMessageCenterOutreachResponse,
  mapMessageCenterThreadRow,
  mapMessageCenterMessage,
  mergeMessageCenterMessages,
  sortMessageCenterMessages,
  enrichMcThreadsWithCampaignCreators,
  patchMcThreadCreatorFields,
  normalizeSenderPlatform,
} from './OutreachUtils';
import {
  PARTIAL_CHECK_STATE,
  isFreshBrandSafetyResult,
  readBrandSafetyCheck,
  readCreatorReportDetailsId,
} from '../components/RunBrandSafetyCheckModal/brandSafetyContract';

// Per-creator check statuses that resolve the optimistic in-flight run (see applyBrandSafetyChecksUpdate).
const BS_TERMINAL_STATES = ['succeeded', PARTIAL_CHECK_STATE, 'failed', 'cancelled'];

// Resolve one optimistic in-flight run and advance its batch tally. Shared by the REST-driven
// finishBrandSafetyRun and the live applyBrandSafetyChecksUpdate so BOTH completion paths keep the
// aggregate-toast accounting honest — whichever observes completion first (the `.checks.updated`
// event usually beats the 10s REST poll). Operates on the Immer draft; no-op for an unknown run.
const resolveBrandSafetyRun = (state, creatorReportDetailsId, ok) => {
  const key = String(creatorReportDetailsId);
  const entry = state.brandSafetyRunsInFlight[key];
  if (!entry) return;

  delete state.brandSafetyRunsInFlight[key];

  const batch = entry.batchId ? state.brandSafetyBatches[entry.batchId] : null;
  if (!batch) return;
  if (ok) batch.completed += 1;
  else batch.failed += 1;
};

const initialState = {
  // influencer tab
  influencers: [],
  influencersFetchedCampaignId: null,

  // brand safety check (AAPU-6214)
  // Optimistic per-creator progress, keyed by String(creatorReportDetailsId).
  // { [crd]: { startedAt: number, percent: number|null, batchId: string } }
  brandSafetyRunsInFlight: {},
  // Aggregate accounting so a bulk run reports one completion toast, not N.
  // { [batchId]: { total: number, completed: number, failed: number } }
  brandSafetyBatches: {},

  outreachEmailAddresses: [],
  // Single source of truth for per-creator outreach emails, keyed by
  // creatorReportDetailsId. Both the influencer drawer (OverviewTab/AddEmailModal)
  // and Group Outreach read from this map so email CRUD stays in sync across views.
  outreachEmailsByCreator: {},

  outreachNotesData: {},

  docLibTemplates: [],

  // documents tab (contracting templates)
  documents: [],
  documentsLoading: false,
  documentsFetchedCampaignId: null,

  // document types (for "Add New Document" menu) — /contracting/document-types
  documentTypes: [],
  documentTypesLoading: false,
  documentTypesFetched: false,

  googleDocumentList: [],

  emailMassOutreachList: [],

  // deliverables tab

  deliverableConversation: {},

  creatorRevisionData: {},
  creatorConversation: {},
  creatorDocuments: [],

  // message center tab
  messageCenterCreators: [],
  messageCenterSelectedCreatorId: null,
  messageCenterOutreachData: emptyMessageCenterOutreachData,
  initMessageCenterOutreachLoading: false,
  messageCenterNotes: [],
  messageCenterNotesLoading: false,
  messageCenterDocuments: [],
  messageCenterDocumentsLoading: false,
  messageCenterDeliverables: [],
  messageCenterDeliverablesLoading: false,
  messageCenterBudget: null,
  messageCenterBudgetLoading: false,
  messageCenterPostPricing: null,
  messageCenterPostPricingLoading: false,
  messageCenterAgreement: null,
  messageCenterAgreementLoading: false,
  updateCreatorStatusLoading: false,
  updateAgreementLoading: false,
  // Message Center v2 (realtime API — FE_MESSAGE_CENTER_GUIDE)
  mcThreads: [],
  mcThreadsLoading: false,
  mcThreadsFetchedId: null,
  mcDefaultSender: null,
  mcUnread: {},
  mcTotalUnread: 0,
  mcOpenThreadId: null,
  mcOpenThreadHeader: null,
  mcMessages: [],
  mcMessagesLoading: false,
  mcMessagesPage: 0,
  mcMessagesLastPage: 1,
  mcMessagesLoadingOlder: false,
  mcWatermark: 0,
  deliverableContentTypes: [],
};

const OutreachSlice = createSlice({
  name: 'Outreach',
  initialState,
  reducers: {
    setOutreachState(state, action) {
      const { key, value } = action.payload;
      state[key] = value;
    },
    // Bump a thread row locally on an inbound message when its thread is NOT open
    // (unread dot + move to top), without a full refetch.
    bumpMcThreadRow(state, action) {
      const { outreachId, lastMessageId } = action.payload;
      const idx = state.mcThreads.findIndex((t) => String(t.outreachId) === String(outreachId));
      if (idx === -1) return;
      const row = state.mcThreads[idx];
      row.unreadCount = (Number(row.unreadCount) || 0) + 1;
      row.notOutreached = false;
      if (lastMessageId != null) row.lastMessageId = lastMessageId;
      state.mcUnread[outreachId] = row.unreadCount;
      state.mcThreads.splice(idx, 1);
      state.mcThreads.unshift(row);
    },
    // Apply .creator.status.changed (Accept/Reject from any source).
    applyMcStatusChange(state, action) {
      const changes = action.payload || [];
      changes.forEach(({ creatorReportDetailsId, status }) => {
        const targetId = String(creatorReportDetailsId);
        state.mcThreads.forEach((t) => {
          if (String(t.creatorReportDetailsId) === targetId) {
            t.status = status;
          }
        });
        const header = state.mcOpenThreadHeader;
        if (header && String(header.creatorReportDetailsId) === targetId) {
          header.creatorStatus = status;
        }
      });
    },
    // Apply .thread.read (only for the current user — caller checks accountId).
    applyMcWatermark(state, action) {
      const { outreachId, lastReadMessageId } = action.payload;
      const idx = state.mcThreads.findIndex((t) => String(t.outreachId) === String(outreachId));
      if (idx !== -1) {
        state.mcThreads[idx].unreadCount = 0;
      }
      state.mcUnread[outreachId] = 0;
      if (String(state.mcOpenThreadId) === String(outreachId)) {
        state.mcWatermark = Math.max(state.mcWatermark || 0, Number(lastReadMessageId) || 0);
      }
    },
    // Apply .message.sent / .message.failed to a message already on screen.
    // The after_id delta only returns messages NEWER than the last one we hold,
    // so a status flip on a rendered bubble never comes back from a refetch —
    // it has to be written from the event payload (FE_GMAIL_QUEUED_SEND.md §5).
    applyMcMessageStatus(state, action) {
      const { messageId, status, failureReason } = action.payload || {};
      if (messageId == null) return;
      const idx = state.mcMessages.findIndex((m) => String(m.id) === String(messageId));
      if (idx === -1) return; // not on screen — the delta will bring it with its status
      const current = state.mcMessages[idx];
      state.mcMessages[idx] = {
        ...current,
        status: status || current.status,
        failureReason: failureReason != null ? failureReason : current.failureReason || null,
      };
    },
    clearMcOpenThread(state) {
      state.mcOpenThreadId = null;
      state.mcOpenThreadHeader = null;
      state.mcMessages = [];
      state.mcMessagesPage = 0;
      state.mcMessagesLastPage = 1;
      state.mcMessagesLoadingOlder = false;
      state.mcWatermark = 0;
    },
    // First outreach email sent — thread is live; switch sidebar + chat off compose mode.
    applyMcThreadOutreached(state, action) {
      const { creatorReportDetailsId, outreachId } = action.payload;
      const targetCrd = String(creatorReportDetailsId);

      const updateRow = (row) => {
        if (String(row.creatorReportDetailsId) !== targetCrd) return row;
        const nextOutreachId = outreachId ?? row.outreachId;
        return {
          ...row,
          outreachId: nextOutreachId,
          notOutreached: false,
          outreachInitiated: '1',
          id: String(nextOutreachId ?? row.creatorReportDetailsId ?? ''),
        };
      };

      state.mcThreads = state.mcThreads.map(updateRow);
      state.messageCenterCreators = state.messageCenterCreators.map(updateRow);

      const selectedId = state.messageCenterSelectedCreatorId;
      if (selectedId && String(selectedId) === targetCrd && outreachId) {
        state.messageCenterSelectedCreatorId = String(outreachId);
      }
    },
    setInfluencers(state, action) {
      state.influencers = action.payload;
    },
    setInfluencersFetchedCampaignId(state, action) {
      state.influencersFetchedCampaignId = action.payload;
    },

    // --- brand safety check (AAPU-6214) ---
    // `startedAt` is passed in rather than read from the clock here so the
    // reducer stays pure.
    startBrandSafetyRuns(state, action) {
      const { creatorReportDetailsIds = [], batchId, startedAt } = action.payload || {};
      if (creatorReportDetailsIds.length === 0) return;

      const rows = Array.isArray(state.influencers) ? state.influencers : [];
      creatorReportDetailsIds.forEach((crd) => {
        const key = String(crd);
        // Capture the row's pre-run result (still its old succeeded status at this optimistic point) so
        // the column can keep showing a prior Safe/Alerts tag while the re-run is in flight instead of
        // blanking it to "Running". Cleared with the whole entry when the run resolves.
        const row = rows.find((r) => readCreatorReportDetailsId(r) === key);
        const priorCheck = readBrandSafetyCheck(row);
        state.brandSafetyRunsInFlight[key] = { startedAt, percent: null, batchId, priorCheck };
      });

      state.brandSafetyBatches[batchId] = {
        total: creatorReportDetailsIds.length,
        completed: 0,
        failed: 0,
      };
    },

    setBrandSafetyRunProgress(state, action) {
      const { creatorReportDetailsId, percent } = action.payload || {};
      const key = String(creatorReportDetailsId);
      // Write only if the run is known, so late or stray events for a run we
      // already finished cannot resurrect it.
      if (!state.brandSafetyRunsInFlight[key]) return;
      state.brandSafetyRunsInFlight[key].percent = percent;
    },

    finishBrandSafetyRun(state, action) {
      const { creatorReportDetailsId, ok } = action.payload || {};
      resolveBrandSafetyRun(state, creatorReportDetailsId, ok);
    },

    /** Pass a list of ids to clear those, or nothing to clear everything. */
    clearBrandSafetyRuns(state, action) {
      const ids = action.payload?.creatorReportDetailsIds;
      if (!ids) {
        state.brandSafetyRunsInFlight = {};
        return;
      }
      ids.forEach((crd) => {
        delete state.brandSafetyRunsInFlight[String(crd)];
      });
    },

    clearBrandSafetyBatch(state, action) {
      delete state.brandSafetyBatches[action.payload];
    },

    /**
     * Apply a live `.checks.updated` event to the influencer rows: patch each matching row's
     * `brandSafetyCheck = { pipelineCheck, userCheck }` in place — the SAME shape (and merge priority,
     * see `mergeBrandSafetyCheck`) `readBrandSafetyCheck` reads for a fetchCreators row, so a live push
     * renders identically to a REST refetch — and clear the optimistic in-flight run once the merged
     * check reaches a terminal state. Payload = the event's `creatorChecks` array (`readChecksUpdatedEvent`
     * output: `{ crd, status, flags, pipelineCheck, userCheck }`). No-op when nothing matches.
     */
    applyBrandSafetyChecksUpdate(state, action) {
      const creatorChecks = Array.isArray(action.payload) ? action.payload : [];
      if (!creatorChecks.length) return;

      const byCrd = {};
      creatorChecks.forEach((check) => {
        if (check && check.crd != null) byCrd[String(check.crd)] = check;
      });

      (Array.isArray(state.influencers) ? state.influencers : []).forEach((row) => {
        const crd = readCreatorReportDetailsId(row);
        const update = byCrd[crd];
        if (!update) return;
        row.brandSafetyCheck = {
          pipelineCheck: update.pipelineCheck ?? null,
          userCheck: update.userCheck ?? null,
        };
        if (BS_TERMINAL_STATES.includes(update.status)) {
          // A re-run of an already-terminal (failed/succeeded) creator carries its OLD result until the
          // backend finishes; a stale snapshot still reporting that pre-run result must NOT resolve the
          // just-started run (that would delete the optimistic entry and stop the poll). Only a genuinely
          // new result vs. the run's captured `priorCheck` resolves it. No in-flight entry ⇒ resolve is a
          // no-op anyway, so leave that path unchanged.
          const entry = state.brandSafetyRunsInFlight[crd];
          if (entry && !isFreshBrandSafetyResult(readBrandSafetyCheck(row), entry.priorCheck)) return;
          // Resolve the optimistic run AND advance its batch tally (via the shared helper) so the
          // aggregate completion toast + credit refresh still fire when the live event — not the REST
          // reconcile — is what observes completion. `cancelled` counts as a failure, like a failed run;
          // a partial check has results, so it counts as a completion.
          resolveBrandSafetyRun(
            state,
            crd,
            ['succeeded', PARTIAL_CHECK_STATE].includes(update.status)
          );
        }
      });
    },

    setOutreachEmailAddressesData(state, action) {
      state.outreachEmailAddresses = Array.isArray(action.payload) ? action.payload : [];
    },

    // Set the canonical email list for a single creator in the shared map.
    setOutreachEmailsForCreator(state, action) {
      const { creatorReportDetailsId, emails } = action.payload;
      if (creatorReportDetailsId == null) return;
      state.outreachEmailsByCreator[String(creatorReportDetailsId)] = Array.isArray(emails)
        ? emails
        : [];
    },

    setOutreachNotesData(state, action) {
      state.outreachNotesData = action.payload;
    },

    setDocLibTemplates(state, action) {
      state.docLibTemplates = action.payload;
    },

    setGoogleDocumentList(state, action) {
      state.googleDocumentList = action.payload;
    },

    setEmailMassOutreachList(state, action) {
      state.emailMassOutreachList = action.payload;
    },

    setDeliverableConversation(state, action) {
      state.deliverableConversation = action.payload;
    },

    setCreatorRevisionData(state, action) {
      state.creatorRevisionData = action.payload;
    },
    setCreatorConversation(state, action) {
      state.creatorConversation = action.payload;
    },
    setCreatorDocuments(state, action) {
      state.creatorDocuments = action.payload;
    },
  },
});

export const {
  setOutreachState,
  bumpMcThreadRow,
  applyMcStatusChange,
  applyMcWatermark,
  applyMcMessageStatus,
  clearMcOpenThread,
  applyMcThreadOutreached,
  setInfluencers,
  setInfluencersFetchedCampaignId,
  startBrandSafetyRuns,
  setBrandSafetyRunProgress,
  finishBrandSafetyRun,
  clearBrandSafetyRuns,
  clearBrandSafetyBatch,
  applyBrandSafetyChecksUpdate,
  setOutreachEmailAddressesData,
  setOutreachEmailsForCreator,
  setOutreachNotesData,
  setDocLibTemplates,
  setGoogleDocumentList,
  setEmailMassOutreachList,
  setDeliverableConversation,
  setCreatorRevisionData,
  setCreatorConversation,
  setCreatorDocuments,
} = OutreachSlice.actions;

/**
 * Initialize outreach thread for the selected creator (Message Center chat).
 */
export const initMessageCenterOutreach = (creatorReportDetailsId) => (dispatch) => {
  if (_.isNil(creatorReportDetailsId)) {
    return Promise.resolve();
  }

  dispatch(setOutreachState({ key: 'initMessageCenterOutreachLoading', value: true }));

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(
          setOutreachState({
            key: 'messageCenterOutreachData',
            value: normalizeMessageCenterOutreachResponse(resp),
          })
        );
      }

      dispatch(setOutreachState({ key: 'initMessageCenterOutreachLoading', value: false }));
      resolve();
    };

    arClient.doApiCall('/outreach/init', { creatorReportDetailsId }, callback);
  });
};

export const setMessageCenterSelectedCreator = (creatorId) => (dispatch) => {
  dispatch(setOutreachState({ key: 'messageCenterSelectedCreatorId', value: creatorId }));
  dispatch(
    setOutreachState({
      key: 'messageCenterOutreachData',
      value: emptyMessageCenterOutreachData,
    })
  );
};

export const fetchMessageCenterNotes = (creatorReportDetailsId) => (dispatch) => {
  if (_.isNil(creatorReportDetailsId)) return;

  dispatch(setOutreachState({ key: 'messageCenterNotes', value: [] }));
  dispatch(setOutreachState({ key: 'messageCenterNotesLoading', value: true }));

  const callback = (resp) => {
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      try {
        const notes = resp.data?.[0]?.notes ? JSON.parse(resp.data[0].notes) : [];
        dispatch(
          setOutreachState({ key: 'messageCenterNotes', value: Array.isArray(notes) ? notes : [] })
        );
      } catch {
        dispatch(setOutreachState({ key: 'messageCenterNotes', value: [] }));
      }
    }
    dispatch(setOutreachState({ key: 'messageCenterNotesLoading', value: false }));
  };

  arClient.doApiCall(
    '/creator/getNotes',
    { creatorReportsDetailId: creatorReportDetailsId },
    callback
  );
};

export const addMessageCenterNote = (creatorReportDetailsId, notes) => (dispatch) => {
  if (_.isNil(creatorReportDetailsId))
    return Promise.reject(new Error('Missing creatorReportDetailsId'));

  return new Promise((resolve, reject) => {
    dispatch(setOutreachState({ key: 'messageCenterNotesLoading', value: true }));

    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(setOutreachState({ key: 'messageCenterNotes', value: notes }));
        dispatch(setOutreachState({ key: 'messageCenterNotesLoading', value: false }));
        resolve();
      } else {
        dispatch(setOutreachState({ key: 'messageCenterNotesLoading', value: false }));
        reject(new Error('Failed to save note'));
      }
    };

    arClient.doApiCall(
      '/creator/addnotes',
      { creatorReportsDetailId: creatorReportDetailsId, note: JSON.stringify(notes) },
      callback
    );
  });
};

/**
 * Fetch contracting templates (documents) for the current campaign.
 * API: POST /contracting/getTemplates { campaignId }
 * Mirrors getContractingTemplates in Campaigns.slice but keeps Outreach state isolated.
 */
export const fetchMessageCenterDocuments = (campaignId) => (dispatch) => {
  if (_.isNil(campaignId)) return;

  dispatch(setOutreachState({ key: 'messageCenterDocumentsLoading', value: true }));

  const callback = (resp) => {
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      const docs = resp.data?.data || resp.data || [];
      dispatch(
        setOutreachState({
          key: 'messageCenterDocuments',
          value: Array.isArray(docs) ? docs : [],
        })
      );
    }
    dispatch(setOutreachState({ key: 'messageCenterDocumentsLoading', value: false }));
  };

  arClient.doApiCall('/contracting/getTemplates', { campaignId }, callback);
};

export const fetchMessageCenterDeliverables = (creatorReportDetailsId, campaignId) => (
  dispatch
) => {
  if (_.isNil(creatorReportDetailsId) || _.isNil(campaignId)) return;

  dispatch(setOutreachState({ key: 'messageCenterDeliverablesLoading', value: true }));
  dispatch(setOutreachState({ key: 'messageCenterDeliverables', value: [] }));

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const callback = (resp) => {
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      try {
        const contentJson = resp.data?.contentJson;
        const parsed =
          typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson ?? {};
        const flat = [];
        Object.entries(parsed).forEach(([platform, types]) => {
          Object.entries(types).forEach(([type, { count }]) => {
            if (count > 0) {
              flat.push({
                id: `${platform}-${type}`,
                platform,
                type,
                label: `${capitalize(platform)} ${capitalize(type)}`,
                count,
              });
            }
          });
        });
        dispatch(setOutreachState({ key: 'messageCenterDeliverables', value: flat }));
      } catch {
        dispatch(setOutreachState({ key: 'messageCenterDeliverables', value: [] }));
      }
    }
    dispatch(setOutreachState({ key: 'messageCenterDeliverablesLoading', value: false }));
  };

  arClient.doApiCall('/agreement/get', { creatorReportDetailsId, campaignId }, callback);
};

export const selectMessageCenterOutreachData = (state) => state.Outreach.messageCenterOutreachData;
export const selectInitMessageCenterOutreachLoading = (state) =>
  state.Outreach.initMessageCenterOutreachLoading;

export const selectMessageCenterSelectedCreator = (state) => {
  const selectedId = state.Outreach.messageCenterSelectedCreatorId;
  if (!selectedId) return null;
  const creators = state.Outreach.messageCenterCreators;
  return (
    creators.find((c) => String(c.id) === String(selectedId)) ||
    creators.find((c) => String(c.creatorReportDetailsId) === String(selectedId)) ||
    null
  );
};

export const selectMessageCenterNotes = (state) => state.Outreach.messageCenterNotes;
export const selectMessageCenterNotesLoading = (state) => state.Outreach.messageCenterNotesLoading;

export const selectMessageCenterDocuments = (state) => state.Outreach.messageCenterDocuments;
export const selectMessageCenterDocumentsLoading = (state) =>
  state.Outreach.messageCenterDocumentsLoading;

export const selectMessageCenterDeliverables = (state) => state.Outreach.messageCenterDeliverables;
export const selectMessageCenterDeliverablesLoading = (state) =>
  state.Outreach.messageCenterDeliverablesLoading;

export const fetchMessageCenterBudget = (creatorReportDetailsId) => (dispatch) => {
  if (_.isNil(creatorReportDetailsId)) return;

  dispatch(setOutreachState({ key: 'messageCenterBudgetLoading', value: true }));

  const callback = (resp) => {
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      const raw = Array.isArray(resp.data) ? resp.data[0] : resp.data;
      dispatch(setOutreachState({ key: 'messageCenterBudget', value: raw ?? null }));
    }
    dispatch(setOutreachState({ key: 'messageCenterBudgetLoading', value: false }));
  };

  arClient.doApiCall('/creator/getBudget', { creatorReportDetailsId }, callback);
};

export const saveMessageCenterBudget = (payload) => (dispatch) => {
  if (_.isNil(payload?.creatorReportDetailsId)) {
    return Promise.reject(new Error('Missing creatorReportDetailsId'));
  }

  return new Promise((resolve, reject) => {
    dispatch(setOutreachState({ key: 'messageCenterBudgetLoading', value: true }));

    const callback = (resp) => {
      dispatch(setOutreachState({ key: 'messageCenterBudgetLoading', value: false }));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/creator/setBudgets', payload, callback);
  });
};

export const selectMessageCenterBudget = (state) => state.Outreach.messageCenterBudget;
export const selectMessageCenterBudgetLoading = (state) =>
  state.Outreach.messageCenterBudgetLoading;

export const fetchMessageCenterPostPricing = (creatorReportDetailsId, requestedPrice = 100) => (
  dispatch
) => {
  if (_.isNil(creatorReportDetailsId)) return Promise.resolve(null);

  dispatch(setOutreachState({ key: 'messageCenterPostPricingLoading', value: true }));

  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const payload = resp.data?.data ?? resp.data ?? null;
        dispatch(setOutreachState({ key: 'messageCenterPostPricing', value: payload }));
        resolve(payload);
      } else {
        reject(resp);
      }
      dispatch(setOutreachState({ key: 'messageCenterPostPricingLoading', value: false }));
    };

    arClient.doApiCall(
      '/creator/getPostPricing',
      { requestedPrice, creatorReportDetailsId },
      callback
    );
  });
};

export const selectMessageCenterPostPricing = (state) => state.Outreach.messageCenterPostPricing;
export const selectMessageCenterPostPricingLoading = (state) =>
  state.Outreach.messageCenterPostPricingLoading;

const isOkStatus = (resp) =>
  resp?.status === LEGACY_OK || resp?.status === OK || resp?.status === 200;

const resolveMcCreatorStatusIds = (
  state,
  { creatorReportDetailsId, builderRequestId, segmentId }
) => {
  const targetId = String(creatorReportDetailsId);
  const fromRow =
    state.messageCenterCreators.find((c) => String(c.creatorReportDetailsId) === targetId) ||
    state.mcThreads.find((t) => String(t.creatorReportDetailsId) === targetId);
  const header =
    String(state.mcOpenThreadHeader?.creatorReportDetailsId) === targetId
      ? state.mcOpenThreadHeader
      : null;

  return {
    builderRequestId:
      builderRequestId ?? header?.builderRequestId ?? fromRow?.builderRequestId ?? null,
    segmentId: segmentId ?? header?.segmentId ?? fromRow?.segmentId ?? null,
  };
};

/**
 * Update a creator's outreach status (e.g. "deliverables-confirmed").
 * API: POST /creator/updateStatus { builderRequestId, segmentId, statusObj }
 * Mirrors updateInfluStatus in Personas.slice. Also syncs the local creators list
 * so the chat header buttons reflect the new status without a full refetch.
 */
export const updateCreatorStatus = ({
  builderRequestId,
  segmentId,
  creatorReportDetailsId,
  status,
}) => (dispatch, getState) => {
  const resolved = resolveMcCreatorStatusIds(getState().Outreach, {
    creatorReportDetailsId,
    builderRequestId,
    segmentId,
  });

  if (_.isNil(creatorReportDetailsId) || _.isNil(resolved.builderRequestId)) {
    return Promise.reject(new Error('Missing creator identifiers'));
  }

  dispatch(setOutreachState({ key: 'updateCreatorStatusLoading', value: true }));

  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setOutreachState({ key: 'updateCreatorStatusLoading', value: false }));
      if (isOkStatus(resp)) {
        dispatch(applyMcStatusChange([{ creatorReportDetailsId, status }]));
        const creators = getState().Outreach.messageCenterCreators;
        const targetId = String(creatorReportDetailsId);
        const synced = creators.map((c) => {
          if (String(c.creatorReportDetailsId) !== targetId) return c;
          return { ...c, status };
        });
        dispatch(setOutreachState({ key: 'messageCenterCreators', value: synced }));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/creator/updateStatus',
      {
        builderRequestId: resolved.builderRequestId,
        segmentId: resolved.segmentId,
        statusObj: JSON.stringify([{ creatorReportsDetailId: creatorReportDetailsId, status }]),
      },
      callback
    );
  });
};

/**
 * Fetch the agreement (deliverables contentJson) for a creator.
 * API: POST /agreement/get { creatorReportDetailsId, campaignId }
 * Stores the raw agreement record (with contentJson) for OutreachAgreementModal.
 */
export const fetchMessageCenterAgreement = (creatorReportDetailsId, campaignId) => (dispatch) => {
  if (_.isNil(creatorReportDetailsId) || _.isNil(campaignId)) return Promise.resolve(null);

  dispatch(setOutreachState({ key: 'messageCenterAgreementLoading', value: true }));

  return new Promise((resolve) => {
    const callback = (resp) => {
      const data = isOkStatus(resp) ? resp.data ?? null : null;
      if (isOkStatus(resp)) {
        dispatch(setOutreachState({ key: 'messageCenterAgreement', value: data }));
      }
      dispatch(setOutreachState({ key: 'messageCenterAgreementLoading', value: false }));
      resolve(data);
    };

    arClient.doApiCall('/agreement/get', { creatorReportDetailsId, campaignId }, callback);
  });
};

/**
 * Persist the deliverables contentJson for a creator.
 * API: POST /agreement/updateContentJson { creatorReportDetailsId, campaignId, contentJson }
 * Mirrors updateAgreementContent in Campaigns.slice (contentJson is JSON-stringified).
 */
export const updateMessageCenterAgreementContentJson = (
  creatorReportDetailsId,
  campaignId,
  contentJson
) => (dispatch) => {
  if (_.isNil(creatorReportDetailsId) || _.isNil(campaignId)) {
    return Promise.reject(new Error('Missing creator identifiers'));
  }

  dispatch(setOutreachState({ key: 'updateAgreementLoading', value: true }));

  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setOutreachState({ key: 'updateAgreementLoading', value: false }));
      if (isOkStatus(resp)) {
        dispatch(setOutreachState({ key: 'messageCenterAgreement', value: resp.data ?? null }));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/agreement/updateContentJson',
      {
        creatorReportDetailsId,
        campaignId,
        contentJson: JSON.stringify(contentJson),
      },
      callback
    );
  });
};

export const selectMessageCenterAgreement = (state) => state.Outreach.messageCenterAgreement;
export const selectMessageCenterAgreementLoading = (state) =>
  state.Outreach.messageCenterAgreementLoading;
export const selectUpdateCreatorStatusLoading = (state) =>
  state.Outreach.updateCreatorStatusLoading;
export const selectUpdateAgreementLoading = (state) => state.Outreach.updateAgreementLoading;

// ============================================================================
// Message Center v2 — realtime API (FE_MESSAGE_CENTER_GUIDE)
// REST is the source of truth; the WebSocket only nudges WHEN to refetch.
// ============================================================================

const buildQuery = (params = {}) => {
  const usable = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  if (usable.length === 0) return '';
  const qs = usable.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  return `?${qs}`;
};

/**
 * §4.1 Thread list (sidebar). Server-side search/filter; paginated envelope.
 */
export const fetchMcThreads = (campaignId, { search, filter, silent = false } = {}) => (
  dispatch
) => {
  if (_.isNil(campaignId)) return Promise.resolve();

  if (!silent) dispatch(setOutreachState({ key: 'mcThreadsLoading', value: true }));

  const query = buildQuery({ search, filter, per_page: 200 });

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (isOkStatus(resp) && resp.data) {
        const rows = resp.data.threads?.data ?? [];
        const mapped = rows.map(mapMessageCenterThreadRow);

        const finish = (enriched) => {
          dispatch(setOutreachState({ key: 'mcThreads', value: enriched }));
          dispatch(setOutreachState({ key: 'messageCenterCreators', value: enriched }));
          dispatch(setOutreachState({ key: 'mcThreadsFetchedId', value: String(campaignId) }));
          if (resp.data.defaultSenderAddress !== undefined) {
            dispatch(
              setOutreachState({ key: 'mcDefaultSender', value: resp.data.defaultSenderAddress })
            );
          }
          if (!silent) dispatch(setOutreachState({ key: 'mcThreadsLoading', value: false }));
          resolve(resp);
        };

        // Threads API omits builderRequestId/segmentId — merge from fetchCreators for updateStatus.
        arClient.doApiCall('/campaign/fetchCreators', { campaignId }, (creatorsResp) => {
          const enriched =
            isOkStatus(creatorsResp) && Array.isArray(creatorsResp.data)
              ? enrichMcThreadsWithCampaignCreators(mapped, creatorsResp.data)
              : mapped;
          finish(enriched);
        });
        return;
      }
      if (!silent) {
        dispatch(setOutreachState({ key: 'mcThreadsLoading', value: false }));
        // Mark attempt complete so the UI does not retry in a loop when the endpoint is down.
        dispatch(setOutreachState({ key: 'mcThreadsFetchedId', value: String(campaignId) }));
      }
      resolve(resp);
    };

    arClient.doApiCallGet(`/campaigns/${campaignId}/message-center/threads${query}`, callback);
  });
};

/**
 * §4.4 Unread counts — page open, reconnect, or socket-down poll ONLY.
 */
export const fetchMcUnreadCounts = (campaignId) => (dispatch) => {
  if (_.isNil(campaignId)) return Promise.resolve();

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (isOkStatus(resp) && resp.data) {
        const map = {};
        (resp.data.threads || []).forEach((t) => {
          map[t.outreachId] = Number(t.unreadCount) || 0;
        });
        dispatch(setOutreachState({ key: 'mcUnread', value: map }));
        dispatch(
          setOutreachState({ key: 'mcTotalUnread', value: Number(resp.data.totalUnread) || 0 })
        );
      }
      resolve(resp);
    };

    arClient.doApiCallGet(`/campaigns/${campaignId}/message-center/unread-counts`, callback);
  });
};

const MC_MESSAGES_PAGE_SIZE = 25;

/**
 * §4.2 Open a thread: paged messages (newest-first) + thread header + watermark.
 */
export const openMcThread = (outreachId) => (dispatch, getState) => {
  if (_.isNil(outreachId)) return Promise.resolve();

  dispatch(setOutreachState({ key: 'mcOpenThreadId', value: String(outreachId) }));
  dispatch(setOutreachState({ key: 'mcMessagesLoading', value: true }));
  dispatch(setOutreachState({ key: 'mcMessagesPage', value: 0 }));
  dispatch(setOutreachState({ key: 'mcMessagesLastPage', value: 1 }));
  dispatch(setOutreachState({ key: 'mcMessagesLoadingOlder', value: false }));

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (isOkStatus(resp) && resp.data) {
        const header = resp.data.thread ?? null;
        const creatorName = header?.creator?.name || 'Influencer';
        const pageEnvelope = resp.data.messages ?? {};
        const raw = pageEnvelope.data ?? [];
        const mapped = sortMessageCenterMessages(
          raw.map((m) => mapMessageCenterMessage(m, creatorName))
        );
        dispatch(setOutreachState({ key: 'mcOpenThreadHeader', value: header }));
        dispatch(setOutreachState({ key: 'mcMessages', value: mapped }));
        dispatch(setOutreachState({ key: 'mcWatermark', value: Number(resp.data.watermark) || 0 }));
        dispatch(
          setOutreachState({
            key: 'mcMessagesPage',
            value: Number(pageEnvelope.current_page) || 1,
          })
        );
        dispatch(
          setOutreachState({
            key: 'mcMessagesLastPage',
            value: Number(pageEnvelope.last_page) || 1,
          })
        );

        if (header?.creatorReportDetailsId) {
          const patch = {
            builderRequestId: header.builderRequestId,
            segmentId: header.segmentId,
          };
          const outreachState = getState().Outreach;
          dispatch(
            setOutreachState({
              key: 'mcThreads',
              value: patchMcThreadCreatorFields(
                outreachState.mcThreads,
                header.creatorReportDetailsId,
                patch
              ),
            })
          );
          dispatch(
            setOutreachState({
              key: 'messageCenterCreators',
              value: patchMcThreadCreatorFields(
                outreachState.messageCenterCreators,
                header.creatorReportDetailsId,
                patch
              ),
            })
          );
        }
      }
      dispatch(setOutreachState({ key: 'mcMessagesLoading', value: false }));
      resolve(resp);
    };

    arClient.doApiCallGet(
      `/message-center/threads/${outreachId}/messages${buildQuery({
        per_page: MC_MESSAGES_PAGE_SIZE,
      })}`,
      callback
    );
  });
};

/**
 * §4.2 Load the next page of older messages when the user scrolls to the top.
 */
export const fetchMcThreadOlderMessages = (outreachId) => (dispatch, getState) => {
  if (_.isNil(outreachId)) return Promise.resolve();

  const state = getState().Outreach;
  if (String(state.mcOpenThreadId) !== String(outreachId)) return Promise.resolve();
  if (state.mcMessagesLoadingOlder || state.mcMessagesLoading) return Promise.resolve();

  const currentPage = Number(state.mcMessagesPage) || 1;
  const lastPage = Number(state.mcMessagesLastPage) || 1;
  if (currentPage >= lastPage) return Promise.resolve();

  const nextPage = currentPage + 1;
  dispatch(setOutreachState({ key: 'mcMessagesLoadingOlder', value: true }));

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (isOkStatus(resp) && resp.data) {
        const liveState = getState().Outreach;
        if (String(liveState.mcOpenThreadId) !== String(outreachId)) {
          dispatch(setOutreachState({ key: 'mcMessagesLoadingOlder', value: false }));
          resolve(resp);
          return;
        }

        const creatorName = liveState.mcOpenThreadHeader?.creator?.name || 'Influencer';
        const pageEnvelope = resp.data.messages ?? {};
        const raw = pageEnvelope.data ?? [];
        const incoming = raw.map((m) => mapMessageCenterMessage(m, creatorName));

        const resolvedPage = Number(pageEnvelope.current_page) || nextPage;
        const resolvedLastPage =
          incoming.length === 0 ? resolvedPage : Number(pageEnvelope.last_page) || lastPage;

        if (incoming.length > 0) {
          dispatch(
            setOutreachState({
              key: 'mcMessages',
              value: mergeMessageCenterMessages(liveState.mcMessages, incoming),
            })
          );
        }

        dispatch(
          setOutreachState({
            key: 'mcMessagesPage',
            value: resolvedPage,
          })
        );
        dispatch(
          setOutreachState({
            key: 'mcMessagesLastPage',
            value: resolvedLastPage,
          })
        );
      } else {
        // Failed or empty response — stop further pagination attempts for this thread.
        dispatch(
          setOutreachState({
            key: 'mcMessagesLastPage',
            value: currentPage,
          })
        );
      }
      dispatch(setOutreachState({ key: 'mcMessagesLoadingOlder', value: false }));
      resolve(resp);
    };

    arClient.doApiCallGet(
      `/message-center/threads/${outreachId}/messages${buildQuery({
        per_page: MC_MESSAGES_PAGE_SIZE,
        page: nextPage,
      })}`,
      callback
    );
  });
};

/**
 * §4.2 Delta — only messages with id above afterId (ascending). Called after
 * every socket event and once after reconnect. Merges into the open thread.
 */
export const fetchMcThreadDelta = (outreachId, afterId) => (dispatch, getState) => {
  if (_.isNil(outreachId)) return Promise.resolve();

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (isOkStatus(resp) && resp.data) {
        const state = getState().Outreach;
        // Ignore deltas for a thread that is no longer open.
        if (String(state.mcOpenThreadId) !== String(outreachId)) {
          resolve(resp);
          return;
        }
        const creatorName = state.mcOpenThreadHeader?.creator?.name || 'Influencer';
        const incoming = (resp.data.messages || []).map((m) =>
          mapMessageCenterMessage(m, creatorName)
        );
        if (incoming.length > 0) {
          dispatch(
            setOutreachState({
              key: 'mcMessages',
              value: mergeMessageCenterMessages(state.mcMessages, incoming),
            })
          );
        }
        if (resp.data.watermark != null) {
          dispatch(
            setOutreachState({
              key: 'mcWatermark',
              value: Math.max(state.mcWatermark || 0, Number(resp.data.watermark) || 0),
            })
          );
        }
      }
      resolve(resp);
    };

    arClient.doApiCallGet(
      `/message-center/threads/${outreachId}/messages${buildQuery({ after_id: afterId })}`,
      callback
    );
  });
};

/**
 * §4.3 Mark read up to the HIGHEST rendered message id (monotonic on the server).
 */
export const markMcThreadRead = (outreachId, lastReadMessageId) => (dispatch, getState) => {
  if (_.isNil(outreachId) || _.isNil(lastReadMessageId)) return Promise.resolve();

  const state = getState().Outreach;
  if (String(state.mcOpenThreadId) !== String(outreachId)) return Promise.resolve();

  const watermark = Number(state.mcWatermark) || 0;
  const nextId = Number(lastReadMessageId) || 0;
  if (nextId <= watermark) return Promise.resolve();

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (isOkStatus(resp)) {
        dispatch(applyMcWatermark({ outreachId, lastReadMessageId: nextId }));
      }
      resolve(resp);
    };

    arClient.doApiCall(
      `/message-center/threads/${outreachId}/read-state`,
      { lastReadMessageId: nextId },
      callback
    );
  });
};

/**
 * Mark the open thread read up to the highest currently-rendered message id.
 * Skips when watermark already covers all loaded messages (avoids duplicate calls).
 */
export const markMcThreadReadCurrent = (outreachId) => (dispatch, getState) => {
  const state = getState().Outreach;
  if (String(state.mcOpenThreadId) !== String(outreachId)) return Promise.resolve();

  let maxMsgId = 0;
  state.mcMessages.forEach((m) => {
    if ((m.messageId || 0) > maxMsgId) maxMsgId = m.messageId;
  });
  const watermark = Number(state.mcWatermark) || 0;

  if (maxMsgId <= 0 || maxMsgId <= watermark) return Promise.resolve();

  return dispatch(markMcThreadRead(outreachId, maxMsgId));
};

/**
 * §4.5 Sync-on-open — fire & forget; never block UI, never retry.
 */
export const syncMcThread = (outreachId) => () => {
  if (_.isNil(outreachId)) return;
  arClient.doApiCall(`/message-center/threads/${outreachId}/syncs`, {}, () => {});
};

/**
 * Send the first outreach email, then open the thread in MessageCenterChat.
 */
export const sendMessageCenterOutreachEmail = ({
  outreachId,
  to,
  cc,
  bcc,
  subject,
  bodyHTML,
  senderInfo,
  creatorReportDetailsId,
  isInitialEmail = true,
  attachments = [],
}) => (dispatch) => {
  if (!outreachId || !creatorReportDetailsId) {
    return Promise.reject();
  }

  const sanitizedBodyHTML = stripFroalaAttribution(bodyHTML);

  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INIT'
      ) {
        dispatch(initMessageCenterOutreach(creatorReportDetailsId)).then(() => {
          dispatch(applyMcThreadOutreached({ creatorReportDetailsId, outreachId }));
          dispatch(openMcThread(outreachId)).then(() => {
            dispatch(syncMcThread(outreachId));
            resolve(resp);
          });
        });
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/sendEmail',
      {
        outreachId,
        to: JSON.stringify(to || []),
        cc: JSON.stringify(cc || []),
        bcc: JSON.stringify(bcc || []),
        subject,
        bodyHTML: sanitizedBodyHTML,
        senderInfo: JSON.stringify(senderInfo || { platform: 'default', oauthInfoId: null }),
        isInitialEmail,
        attachmentsJSON: JSON.stringify(Array.isArray(attachments) ? attachments : []),
      },
      callback
    );
  });
};

/**
 * §8 Exchange a private attachment filePath for a short-lived signed URL.
 * Uses the existing /outreach/getSignedUrl endpoint (resolves to the signed URL).
 */
export const getMcSignedUrl = (fileURL) => () =>
  new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isOkStatus(resp)) {
        resolve(resp.data?.signedUrl || resp.data?.data?.signedUrl || null);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall('/outreach/getSignedUrl', { fileURL }, callback);
  });

/**
 * §4.7 Reply on an open thread. Outcome arrives via `.message.sent` / `.message.failed`;
 * we also pull the delta on success so the bubble appears without waiting on the socket.
 */
export const sendMcReply = ({
  outreachId,
  bodyHTML,
  to,
  subject,
  senderInfo,
  attachments = [],
}) => (dispatch, getState) => {
  if (_.isNil(outreachId)) return Promise.reject(new Error('Missing outreachId'));

  const sanitizedBodyHTML = stripFroalaAttribution(bodyHTML);

  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isOkStatus(resp) || resp.status === 'INIT') {
        // eslint-disable-next-line no-use-before-define
        const lastId = selectMcLastMessageId(getState());
        dispatch(fetchMcThreadDelta(outreachId, lastId));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/sendEmail',
      {
        outreachId,
        to: JSON.stringify(to || []),
        cc: JSON.stringify([]),
        bcc: JSON.stringify([]),
        subject: subject || '',
        bodyHTML: sanitizedBodyHTML,
        senderInfo: JSON.stringify(senderInfo || { platform: 'default', oauthInfoId: null }),
        isInitialEmail: false,
        attachmentsJSON: JSON.stringify(Array.isArray(attachments) ? attachments : []),
      },
      callback
    );
  });
};

export const selectMcThreads = (state) => state.Outreach.mcThreads;
export const selectMcThreadsLoading = (state) => state.Outreach.mcThreadsLoading;
export const selectMcThreadsFetchedId = (state) => state.Outreach.mcThreadsFetchedId;
export const selectMcOpenThreadId = (state) => state.Outreach.mcOpenThreadId;
export const selectMcOpenThreadHeader = (state) => state.Outreach.mcOpenThreadHeader;
export const selectMcMessages = (state) => state.Outreach.mcMessages;
export const selectMcMessagesLoading = (state) => state.Outreach.mcMessagesLoading;
export const selectMcMessagesLoadingOlder = (state) => state.Outreach.mcMessagesLoadingOlder;
export const selectMcHasOlderMessages = (state) => {
  const page = Number(state.Outreach.mcMessagesPage) || 0;
  const lastPage = Number(state.Outreach.mcMessagesLastPage) || 1;
  return page > 0 && page < lastPage;
};
export const selectDeliverableContentTypes = (state) => state.Outreach.deliverableContentTypes;

/** Highest message id currently rendered in the open thread (delta after_id / read watermark). */
export const selectMcLastMessageId = (state) => {
  const messages = state.Outreach.mcMessages;
  let max = state.Outreach.mcWatermark || 0;
  messages.forEach((m) => {
    if ((m.messageId || 0) > max) max = m.messageId;
  });
  return max;
};

// --- Influencers ---

export const fetchOutreachInfluencers = (campaignId) => (dispatch) => {
  if (!campaignId) return Promise.resolve();

  return new Promise((resolve, reject) => {
    arClient.doApiCall('/campaign/fetchCreators', { campaignId }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(setInfluencers(resp.data || []));
        dispatch(setInfluencersFetchedCampaignId(campaignId));
        resolve(resp.data || []);
      } else {
        reject(resp);
      }
    });
  });
};

// --- brand safety check (AAPU-6214) ---
export const selectBrandSafetyRunsInFlight = (state) => state.Outreach.brandSafetyRunsInFlight;
export const selectBrandSafetyBatches = (state) => state.Outreach.brandSafetyBatches;

export const updateOutreachInfluencerStatus = (builderRequestId, segmentId, statusObj) => () => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/creator/updateStatus',
      {
        builderRequestId,
        segmentId,
        statusObj: JSON.stringify(statusObj),
      },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          resolve(resp);
        } else {
          reject(resp);
        }
      }
    );
  });
};

export const getCreatorBudget = (creatorReportDetailsId) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall('/creator/getBudget', { creatorReportDetailsId }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    });
  });
};

// --- Outreach Emails ---

export const getOutreachEmailAddresses = (creatorReportDetailsId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall('/outreach/getEmailAddresses', { creatorReportDetailsId }, (respApi) => {
      if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
        dispatch(setOutreachEmailAddressesData(respApi.data));
        dispatch(setOutreachEmailsForCreator({ creatorReportDetailsId, emails: respApi.data }));
        resolve(respApi.data);
      } else {
        reject(respApi);
      }
    });
  });
};

export const addOutreachEmailAddresses = (creatorReportDetailsId, emailsArr, id) => async (
  dispatch,
  getState
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const { Outreach } = getState();
        const { outreachEmailAddresses, outreachEmailsByCreator } = Outreach;

        const applyChange = (list) => {
          const base = Array.isArray(list) ? list : [];
          if (id) {
            return base.map((item) =>
              item.id === id
                ? { ...item, role: emailsArr[0].role, email: emailsArr[0].email }
                : item
            );
          }
          return [...base, ...emailsArr];
        };

        // Flat list is drawer-scoped (last fetched creator); keep it for back-compat.
        dispatch(setOutreachEmailAddressesData(applyChange(outreachEmailAddresses)));
        // Keyed map is the cross-view source of truth — always scope to this creator.
        const creatorEmails = outreachEmailsByCreator[String(creatorReportDetailsId)];
        dispatch(
          setOutreachEmailsForCreator({
            creatorReportDetailsId,
            emails: applyChange(creatorEmails),
          })
        );
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/addEmailAddresses',
      {
        creatorReportDetailsId,
        emailsArr: JSON.stringify(emailsArr),
      },
      callback
    );
  });
};

export const editOutreachEmailAddress = (creatorReportDetailsId, id, email, role) => async (
  dispatch,
  getState
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const { Outreach } = getState();
        const { outreachEmailAddresses, outreachEmailsByCreator } = Outreach;
        const editItem = (list) =>
          (Array.isArray(list) ? list : []).map((item) =>
            item.id === id ? { ...item, role, email } : item
          );

        dispatch(setOutreachEmailAddressesData(editItem(outreachEmailAddresses)));
        dispatch(
          setOutreachEmailsForCreator({
            creatorReportDetailsId,
            emails: editItem(outreachEmailsByCreator[String(creatorReportDetailsId)]),
          })
        );
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/editEmailAddress',
      {
        creatorReportDetailsId,
        id,
        email,
        role,
      },
      callback
    );
  });
};

export const deleteOutreachEmailAddress = (id, creatorReportDetailsId) => async (
  dispatch,
  getState
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const { Outreach } = getState();
        const { outreachEmailAddresses, outreachEmailsByCreator } = Outreach;
        dispatch(
          setOutreachEmailAddressesData(outreachEmailAddresses.filter((item) => item.id !== id))
        );

        // Update the keyed map. Use the passed creator id, or fall back to
        // locating whichever creator's list currently holds this email id.
        const targetCrd =
          creatorReportDetailsId != null
            ? String(creatorReportDetailsId)
            : Object.keys(outreachEmailsByCreator).find((crd) =>
                outreachEmailsByCreator[crd].some((item) => item.id === id)
              );
        if (targetCrd && outreachEmailsByCreator[targetCrd]) {
          dispatch(
            setOutreachEmailsForCreator({
              creatorReportDetailsId: targetCrd,
              emails: outreachEmailsByCreator[targetCrd].filter((item) => item.id !== id),
            })
          );
        }
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/outreach/delEmailAddress', { id }, callback);
  });
};

// --- Group Outreach (mass) ---

/**
 * Fetch the connected sender mailboxes for a campaign, with the default RAD
 * address prepended. Resolves the list (sender selection is flow-local UI
 * state, so nothing is written to the store here).
 */
export const fetchOutreachSenderEmails = (campaignId) => () => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const rows = Array.isArray(resp.data) ? resp.data : [];
        // Canonicalize 'google'/'Gmail' → 'gmail' so the exact-match platform
        // checks in the sender dropdowns recognize the connection.
        const emails = rows.map((row) => ({
          ...row,
          platform: normalizeSenderPlatform(row.platform),
        }));
        emails.unshift({ email: OUTREACH_DEFAULT_EMAIL, ...DEFAULT_SENDER_INFO });
        resolve(emails);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/outreach/getSenderEmailAddresses', { campaignId }, callback);
  });
};

/**
 * Send one group outreach email to every selected recipient. `creatorContacts`
 * carries the per-creator recipient list directly (no Redux round-trip), and
 * `{name}` is personalized server-side.
 */
export const sendGroupOutreachEmail = ({
  subject,
  bodyHTML,
  senderInfo = DEFAULT_SENDER_INFO,
  creatorContacts = [],
}) => () => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200 ||
        resp.status === 'INIT'
      ) {
        resolve(true);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/massOutreach',
      {
        subject,
        bodyHTML: stripFroalaAttribution(bodyHTML),
        senderInfo: JSON.stringify(senderInfo || DEFAULT_SENDER_INFO),
        creatorContacts: JSON.stringify(creatorContacts || []),
      },
      callback
    );
  });
};

// --- Internal Notes ---

export const getOutreachNotesData = (creatorReportDetailsId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall('/outreach/getInternalNotes', { creatorReportDetailsId }, (respApi) => {
      if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
        dispatch(setOutreachNotesData(respApi.data));
        resolve(respApi.data);
      } else {
        reject(respApi);
      }
    });
  });
};

export const setOutreachNotes = (creatorReportDetailsId, notesList) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/outreach/setInternalNotes',
      {
        creatorReportDetailsId,
        notesArr: JSON.stringify(notesList),
      },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(getOutreachNotesData(creatorReportDetailsId));
          resolve(resp);
        } else {
          reject(resp);
        }
      }
    );
  });
};

// --- Documents ---

export const getContractingTemplates = (campaignId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const docs = resp.data?.data || resp.data || [];
        resolve(Array.isArray(docs) ? docs : []);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall('/contracting/getTemplates', { campaignId }, callback);
  });
};

/**
 * Documents tab — fetch contracting templates and persist them to the Outreach slice.
 * Wraps the plain getContractingTemplates helper so the Documents index page can read
 * the normalized list + loading flag from selectors (matching the Message Center pattern).
 * API: POST /contracting/getTemplates { campaignId }
 */
export const fetchOutreachDocuments = (campaignId) => (dispatch) => {
  if (_.isNil(campaignId)) return Promise.resolve([]);

  dispatch(setOutreachState({ key: 'documentsLoading', value: true }));

  return getContractingTemplates(campaignId)
    .then((docs) => {
      const mapped = (Array.isArray(docs) ? docs : []).map((doc) => {
        const { influencerList = [] } = doc;
        const recipients = influencerList
          .filter((inf) => inf.username)
          .map((inf) => {
            let avatar = '';
            try {
              avatar = JSON.parse(inf.normalizedRespData)?.userPhotoUrl || '';
            } catch (e) {
              /* ignore parse errors */
            }
            return {
              username: inf.username,
              avatar,
              displayName: inf.username,
              creatorReportDetailsId: inf.creatorReportDetailsId,
            };
          });
        return {
          ...doc,
          documentName: doc.documentName || doc.title || doc.name || 'Untitled',
          documentType: doc.documentType || 'Document',
          createdTimestamp:
            doc.createdTimestamp || doc.createdAt || doc.created_at || doc.updatedAt,
          recipients,
        };
      });
      dispatch(setOutreachState({ key: 'documents', value: mapped }));
      dispatch(setOutreachState({ key: 'documentsFetchedCampaignId', value: String(campaignId) }));
      return mapped;
    })
    .catch(() => {
      dispatch(setOutreachState({ key: 'documents', value: [] }));
      dispatch(setOutreachState({ key: 'documentsFetchedCampaignId', value: String(campaignId) }));
      return [];
    })
    .finally(() => {
      dispatch(setOutreachState({ key: 'documentsLoading', value: false }));
    });
};

export const selectOutreachDocuments = (state) => state.Outreach.documents;
export const selectOutreachDocumentsLoading = (state) => state.Outreach.documentsLoading;
export const selectOutreachDocumentsFetchedId = (state) =>
  state.Outreach.documentsFetchedCampaignId;

// Default document types used when /contracting/document-types is unavailable.
export const DEFAULT_DOCUMENT_TYPES = [
  { id: 1, name: 'Unilateral NDA' },
  { id: 2, name: 'Mutual NDA' },
  { id: 3, name: 'Creative Brief' },
  { id: 4, name: 'Influencer Contract' },
];

export const fetchDocumentTypes = () => (dispatch) => {
  dispatch(setOutreachState({ key: 'documentTypesLoading', value: true }));

  const callback = (resp) => {
    let types = DEFAULT_DOCUMENT_TYPES;
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      const data = resp.data?.data || resp.data || [];
      if (Array.isArray(data) && data.length > 0) {
        types = data;
      }
    }
    dispatch(setOutreachState({ key: 'documentTypes', value: types }));
    // Always flag as fetched (even on error) to prevent a retry loop when the endpoint is down.
    dispatch(setOutreachState({ key: 'documentTypesFetched', value: true }));
    dispatch(setOutreachState({ key: 'documentTypesLoading', value: false }));
  };

  arClient.doApiCallGet('/contracting/document-types', callback);
};

export const selectDocumentTypes = (state) => state.Outreach.documentTypes;
export const selectDocumentTypesLoading = (state) => state.Outreach.documentTypesLoading;
export const selectDocumentTypesFetched = (state) => state.Outreach.documentTypesFetched;

/**
 * Update a contracting document's content (Documents tab edit drawer).
 * API: POST /contracting/updateDocument { id, documentName, documentType, isTemplate, html }
 * Mirrors updateContractingDocument in CampaignDocuments.slice but keeps Outreach isolated.
 */
export const updateOutreachDocument = (payload) => () =>
  new Promise((resolve, reject) => {
    if (_.isNil(payload?.id)) {
      reject(new Error('Missing document id'));
      return;
    }
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall('/contracting/updateDocument', payload, callback);
  });

/**
 * Create a new contracting document (Documents tab "Save as Template" flow).
 * API: POST /contracting/createDocument { documentType, documentName, html, isTemplate }
 */
export const createOutreachDocument = ({
  documentType,
  documentName,
  html,
  isTemplate = 1,
}) => () =>
  new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall(
      '/contracting/createDocument',
      { documentType, documentName, html, isTemplate },
      callback
    );
  });

/**
 * Send a contracting document (NDA / Creative Brief / Influencer Contract) to one or
 * more influencers in a single call. The document type is derived server-side.
 * API: POST /contracting/send-document { documentId, creatorReportDetailsIds, campaignId }
 *
 * Resolves with the full response envelope so the caller can read the overall status
 * (200 all sent, 207 partial, 422/404/500 failure) and per-recipient `dispatched`.
 */
export const sendContractingDocument = ({
  documentId,
  creatorReportDetailsIds,
  campaignId,
}) => () =>
  new Promise((resolve, reject) => {
    if (
      _.isNil(documentId) ||
      !Array.isArray(creatorReportDetailsIds) ||
      creatorReportDetailsIds.length === 0
    ) {
      reject(new Error('Missing documentId or recipients'));
      return;
    }

    const payload = { documentId };
    creatorReportDetailsIds.forEach((id, index) => {
      payload[`creatorReportDetailsIds[${index}]`] = id;
    });
    if (!_.isNil(campaignId)) payload.campaignId = campaignId;

    arClient.doApiCall('/contracting/sendDocument', payload, (resp) => resolve(resp));
  });

export const getGoogleDocsDocuments = (campaignId, creatorReportDetailsId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/google-doc/all-documents',
      { campaignId, creatorReportDetailsId },
      (respApi) => {
        if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
          const docs = respApi.data?.data || respApi.data || [];
          dispatch(setGoogleDocumentList(Array.isArray(docs) ? docs : []));
          resolve(docs);
        } else {
          reject(respApi);
        }
      }
    );
  });
};

// --- Deliverables (plain helper, no Redux state) ---

export const fetchCampaignDeliverables = (campaignId) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/content-revision/fetchAllByCampaignId',
      { campaignId },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          resolve(Array.isArray(resp.data) ? resp.data : []);
        } else {
          reject(resp);
        }
      },
      {}
    );
  });
};

// --- Deliverable Details (conversation) ---

export const getDeliverableConversation = (contentRevisionDetailsId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/content-revision/getConversation',
      { contentRevisionDetailsId },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          if (!_.isEmpty(resp.data)) {
            dispatch(setDeliverableConversation(resp.data));
          }
          resolve(resp);
        } else {
          reject(resp);
        }
      },
      {}
    );
  });
};

export const acceptDeliverable = (contentRevisionDetailsId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      `/content-revision/${contentRevisionDetailsId}/accept`,
      {},
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(getDeliverableConversation(contentRevisionDetailsId));
          resolve(resp);
        } else {
          reject(resp);
        }
      },
      {}
    );
  });
};

export const rejectDeliverable = (contentRevisionDetailsId) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      `/content-revision/${contentRevisionDetailsId}/reject`,
      {},
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(getDeliverableConversation(contentRevisionDetailsId));
          resolve(resp);
        } else {
          reject(resp);
        }
      },
      {}
    );
  });
};

// Cancel replaces delete for a persisted deliverable: the row stays in the DB (and its
// contentJson slot) but is flagged cancelled. Plain promise helper (no auto-refetch) so the
// agreement modal can update its local rows without pulling a conversation it doesn't render;
// the detail view refetches getDeliverableConversation itself after resolving.
export const cancelDeliverable = (deliverableId) =>
  new Promise((resolve, reject) => {
    arClient.doApiCall(
      `/content-revision/deliverable/${deliverableId}/cancel`,
      {},
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          resolve(resp.data);
        } else {
          reject(resp);
        }
      },
      {}
    );
  });

export const clientAddDraftComment = (contentRevisionDetailsId, draftId, message) => async (
  dispatch
) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      `/content-revision/${contentRevisionDetailsId}/draft/${draftId}/comment`,
      { message },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(getDeliverableConversation(contentRevisionDetailsId));
          resolve(resp);
        } else {
          reject(resp);
        }
      },
      {}
    );
  });
};

export const clientSendMessage = (contentRevisionDetailsId, message) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      `/content-revision/${contentRevisionDetailsId}/message`,
      { message },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(getDeliverableConversation(contentRevisionDetailsId));
          resolve(resp);
        } else {
          reject(resp);
        }
      },
      {}
    );
  });
};

export const clearDeliverableConversation = () => (dispatch) => {
  dispatch(setDeliverableConversation({}));
};

// --- Creator-Facing (public, no auth) ---

export const fetchCreatorRevisionList = (contentRevisionId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(CUSTOM_FETCH_REVISIONS, { contentRevisionId }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(setCreatorRevisionData(resp.data || {}));
        resolve(resp.data);
      } else {
        reject(resp);
      }
    });
  });
};

export const fetchCreatorDocuments = (contentRevisionId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(CUSTOM_FETCH_DOCUMENTS, { contentRevisionId }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(setCreatorDocuments(resp.data?.documents || []));
        resolve(resp.data);
      } else {
        reject(resp);
      }
    });
  });
};

export const fetchCreatorConversation = (contentRevisionDetailsId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(CUSTOM_GET_CONVERSATION, { contentRevisionDetailsId }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        if (!_.isEmpty(resp.data)) {
          dispatch(setCreatorConversation(resp.data));
        }
        resolve(resp.data);
      } else {
        reject(resp);
      }
    });
  });
};

export const creatorSubmitDraft = (contentRevisionDetailsId, message, media) => (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(
      CUSTOM_ADD_DRAFT,
      { contentRevisionDetailsId, message, media: JSON.stringify(media) },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(fetchCreatorConversation(contentRevisionDetailsId));
          resolve(resp.data);
        } else {
          reject(resp);
        }
      }
    );
  });
};

export const creatorAddDraftComment = (contentRevisionDetailsId, draftId, message) => (
  dispatch
) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(
      CUSTOM_ADD_DRAFT_COMMENT,
      { contentRevisionDetailsId, draftId, message },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(fetchCreatorConversation(contentRevisionDetailsId));
          resolve(resp.data);
        } else {
          reject(resp);
        }
      }
    );
  });
};

/**
 * Submit a creator draft (media via /custom/add-draft) and, when provided, attach
 * a comment to that draft (/custom/add-draft-comment). The comment is optional;
 * when empty we skip the comment call. The conversation is refreshed once at the end.
 */
export const creatorSubmitDraftWithComment = (contentRevisionDetailsId, comment, media) => (
  dispatch
) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(
      CUSTOM_ADD_DRAFT,
      { contentRevisionDetailsId, message: '', media: JSON.stringify(media) },
      (resp) => {
        if (resp.status !== LEGACY_OK && resp.status !== OK && resp.status !== 200) {
          reject(resp);
          return;
        }

        const refreshAndResolve = () =>
          dispatch(fetchCreatorConversation(contentRevisionDetailsId)).finally(() =>
            resolve(resp.data)
          );

        const trimmed = (comment || '').trim();
        if (!trimmed) {
          refreshAndResolve();
          return;
        }

        const postComment = (draftId) => {
          if (draftId == null) {
            refreshAndResolve();
            return;
          }
          arClient.doPublicApiCall(
            CUSTOM_ADD_DRAFT_COMMENT,
            { contentRevisionDetailsId, draftId, message: trimmed },
            () => refreshAndResolve()
          );
        };

        const draftId = resp.data?.draftId ?? resp.data?.id ?? resp.data?.draft?.id ?? null;
        if (draftId != null) {
          postComment(draftId);
          return;
        }

        // Fallback: the add-draft response didn't include the new draft id, so
        // refresh the conversation and derive the newest draft (highest id).
        dispatch(fetchCreatorConversation(contentRevisionDetailsId))
          .then((conversation) => {
            const newestDraft = (conversation?.details || [])
              .filter((entry) => entry.messageType === 'draft')
              .reduce(
                (max, entry) => (Number(entry.id) > Number(max?.id ?? -Infinity) ? entry : max),
                null
              );
            postComment(newestDraft ? newestDraft.id : null);
          })
          .catch(() => resolve(resp.data));
      }
    );
  });
};

export const creatorSendMessage = (contentRevisionDetailsId, message) => (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(CUSTOM_ADD_MESSAGE, { contentRevisionDetailsId, message }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(fetchCreatorConversation(contentRevisionDetailsId));
        resolve(resp.data);
      } else {
        reject(resp);
      }
    });
  });
};

/** Delete a creator draft (and its comments/media); refreshes the conversation. */
export const creatorDeleteDraft = (contentRevisionDetailsId, draftId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(
      CUSTOM_DELETE_DRAFT,
      { contentRevisionDetailsId, draftId },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(fetchCreatorConversation(contentRevisionDetailsId));
          resolve(resp.data);
        } else {
          reject(resp);
        }
      },
      'DELETE'
    );
  });
};

export const clearCreatorConversation = () => (dispatch) => {
  dispatch(setCreatorConversation({}));
};

// --- Agreements ---

export const getAgreementCount = (creatorReportDetailsId, campaignId) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall('/agreement/get', { creatorReportDetailsId, campaignId }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    });
  });
};

export const setAgreementCount = (creatorReportDetailsId, campaignId, agreementList) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/agreement/create',
      { creatorReportDetailsId, campaignId, contentJson: JSON.stringify(agreementList) },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          resolve(resp.data);
        } else {
          reject(resp);
        }
      }
    );
  });
};

// --- TikTok One (Spark Ads) ---

export const linkWithTTO = (TTOCampaignId, videoId, requestsAction) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/tiktok-one/campaignVideoLink',
      { TTOCampaignId, videoId, requestsAction },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          resolve(resp.data);
        } else {
          reject(resp.error);
        }
      }
    );
  });
};

export const getTTOLinkingStatus = (TTOCampaignId, videoId) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall('/tiktok-one/videoStatus', { TTOCampaignId, videoId }, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    });
  });
};

// --- Creator Publish URL (public, no auth) ---

export const infSubmitPublishURL = ({ contentRevisionDetailsId, publishURL }) => (dispatch) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall(
      '/custom/add-publish-link',
      { contentRevisionDetailsId, publishURL },
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          dispatch(fetchCreatorConversation(contentRevisionDetailsId));
          resolve(resp.data);
        } else {
          reject(resp);
        }
      }
    );
  });
};

// --- Contracts ---

export const sendInfluencerContractInfluencer = (documentId, creatorReportDetailsId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall(
      '/contracting/sendInfluencerContractToInfluencer',
      { documentId, creatorReportDetailsId },
      callback
    );
  });
};

export const addDeliveryInstructions = (instructions, contentRevisionDetailsId) => async () => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      `/content-revision/add-delivery-instructions`,
      {
        instructions: JSON.stringify(instructions),
        contentRevisionDetailsId,
      },
      callback,
      {}
    );
  });
};

export const clientSubmitPublishURL = ({ contentRevisionDetailsId, publishURL }) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
        dispatch(getDeliverableConversation(contentRevisionDetailsId));
      } else {
        reject();
      }
    };

    arClient.doApiCall(
      '/custom/addPublishLink',
      {
        contentRevisionDetailsId,
        publishURL,
      },
      callback
    );
  });
};

/**
 * Deliverable content types (server-owned) for pricing calc + deliverables.
 * Single source of truth for the Content Type dropdown and display labels.
 */
export const fetchDeliverableContentTypes = () => (dispatch) => {
  return new Promise((resolve) => {
    const callback = (resp) => {
      if (isOkStatus(resp) && Array.isArray(resp.data)) {
        dispatch(setOutreachState({ key: 'deliverableContentTypes', value: resp.data }));
      }
      resolve(resp);
    };
    arClient.doApiCallGet('/deliverables/content-types', callback);
  });
};

export default OutreachSlice.reducer;
